import {
  Clock,
  Landmark,
  Loader2,
  Percent,
  SendHorizonal,
  Split,
} from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type DraftPayment,
  DraftPaymentStatus,
  type InvoiceData,
  type Payment,
  PaymentEventStatus,
  type Supplier,
} from "@/apis/gql/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TypographySmall } from "@/components/ui/typography";

import { ibanValidationSchema } from "@/features/invoice-standard-review/validations/payment";
import { Button } from "@/components/ui/button";
import { formatAmount } from "@/lib/utils";
import type {
  GetInvoiceDetailsInvoice,
  InvoiceDetailsDraftPayment,
  InvoiceDetailsSupplier,
} from "@/queries/invoices/get";
import { toast } from "sonner";
import { EditDiscountForm } from "./edit-discount";
import {
  EditExecutionDateForm,
  type EditExecutionDateFormValues,
} from "./edit-execution-date";
import {
  EditPaymentIbanForm,
  type EditPaymentIbanFormValues,
} from "./edit-payment-iban";
import {
  EditPaymentReferenceForm,
  type EditPaymentReferenceFormValues,
} from "./edit-payment-reference";
import {
  PartialPaymentsForm,
  type PartialPaymentsFormValues,
} from "./partial-payments";

type Props = {
  draftPayments: InvoiceDetailsDraftPayment[];
  invoiceId: string;
  invoice: GetInvoiceDetailsInvoice;
  iban: string | null;
  supplier: InvoiceDetailsSupplier;
  recipientName: string;
  currency: string;
  totalAmount: number;
  onFieldFocus: (field: string) => void;
  onPay: () => Promise<void>;
  onValuesChange?: (
    values:
      | EditPaymentReferenceFormValues
      | EditExecutionDateFormValues
      | PartialPaymentsFormValues
      | EditPaymentIbanFormValues,
  ) => void;
};

const _PaymentForm = ({
  draftPayments,
  invoiceId,
  invoice,
  iban,
  currency,
  totalAmount,
  supplier,
  onFieldFocus,
  onPay,
}: Props) => {
  const { t } = useTranslation();

  const [partialPaymentFormIsDirty, setPartialPaymentFormIsDirty] =
    useState(false);
  const [paymentOptionsAccordionOpen, setPaymentOptionsAccordionOpen] =
    useState(false);

  const handlePartialPaymentFormIsDirty = () => {
    setPartialPaymentFormIsDirty(true);
  };

  const uninitiatedDraftPayments = useMemo(
    () =>
      draftPayments.filter(
        (draftPayment) =>
          draftPayment.status &&
          [DraftPaymentStatus.DRAFT].includes(draftPayment.status),
      ),
    [draftPayments],
  );

  const completedPayments = useMemo(
    () =>
      draftPayments.filter(
        (draftPayment) => draftPayment.status === DraftPaymentStatus.INITIATED,
      ),
    [draftPayments],
  );

  const editExecutionDate = Boolean(uninitiatedDraftPayments[0]?.executionDate);

  const editIban = useMemo(() => {
    if (!supplier?.paymentDetails?.IBAN) {
      return true;
    }
    const validIban = ibanValidationSchema.safeParse({
      supplier: {
        paymentDetails: {
          IBAN: supplier?.paymentDetails?.IBAN,
        },
      },
      iban,
    });
    return validIban.error !== undefined;
  }, [supplier?.paymentDetails?.IBAN, iban]);

  const hasPartialPayment = useMemo(() => {
    const paidAmount = uninitiatedDraftPayments.reduce((acc, payment) => {
      return acc + (payment.amount ?? 0);
    }, 0);

    return paidAmount !== totalAmount || partialPaymentFormIsDirty;
  }, [uninitiatedDraftPayments, totalAmount, partialPaymentFormIsDirty]);

  const hasDiscount = uninitiatedDraftPayments.some(
    (payment) => payment.discount,
  );

  const { showExecutionDate, showPartialPayment, showDiscount, showIban } =
    useShowPaymentOptions();

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 mt-4">
        <div className="flex flex-col gap-2">
          <EditPaymentReferenceForm
            draftPayments={uninitiatedDraftPayments}
            invoiceId={invoiceId}
            currency={currency}
            amount={totalAmount}
            onFieldFocus={onFieldFocus}
          />

          {showExecutionDate && (
            <EditExecutionDateForm
              draftPayments={uninitiatedDraftPayments}
              invoice={invoice}
              invoiceId={invoiceId}
              onFieldFocus={onFieldFocus}
            />
          )}

          {showPartialPayment && (
            <PartialPaymentsForm
              draftPayments={uninitiatedDraftPayments}
              completedDraftPayments={completedPayments}
              invoiceId={invoiceId}
              currency={currency}
              totalAmount={totalAmount}
              onValuesChange={handlePartialPaymentFormIsDirty}
            />
          )}

          {showDiscount && (
            <EditDiscountForm
              invoiceId={invoiceId}
              draftPayments={uninitiatedDraftPayments}
              totalAmount={totalAmount}
              onFieldFocus={onFieldFocus}
            />
          )}

          {showIban && (
            <EditPaymentIbanForm
              supplier={supplier}
              invoiceId={invoiceId}
              onFieldFocus={onFieldFocus}
              iban={iban}
            />
          )}

          <Accordion
            type="single"
            collapsible
            className="w-full"
            value={paymentOptionsAccordionOpen ? "payment-options" : ""}
            onValueChange={(value) => {
              if (value === "payment-options") {
                setPaymentOptionsAccordionOpen(true);
              } else {
                setPaymentOptionsAccordionOpen(false);
              }
            }}
          >
            <AccordionItem value="payment-options" className="border-none">
              <AccordionContent>
                <div className="flex flex-col gap-4">
                  {!showPartialPayment && (
                    <PartialPaymentsForm
                      draftPayments={uninitiatedDraftPayments}
                      completedDraftPayments={completedPayments}
                      invoiceId={invoiceId}
                      currency={currency}
                      totalAmount={totalAmount}
                      onValuesChange={handlePartialPaymentFormIsDirty}
                    />
                  )}

                  {!showExecutionDate && (
                    <EditExecutionDateForm
                      draftPayments={uninitiatedDraftPayments}
                      invoice={invoice}
                      invoiceId={invoiceId}
                      onFieldFocus={onFieldFocus}
                    />
                  )}

                  {!showDiscount && (
                    <EditDiscountForm
                      invoiceId={invoiceId}
                      draftPayments={uninitiatedDraftPayments}
                      totalAmount={totalAmount}
                      onFieldFocus={onFieldFocus}
                    />
                  )}

                  {!showIban && (
                    <EditPaymentIbanForm
                      supplier={supplier}
                      invoiceId={invoiceId}
                      onFieldFocus={onFieldFocus}
                      iban={iban}
                    />
                  )}
                </div>
              </AccordionContent>
              <AccordionTrigger className="whitespace-nowrap mt-3 hover:no-underline font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:bg-accent dark:hover:bg-accent/50 h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-sm text-black flex items-center justify-between w-full cursor-pointer">
                <span>{t("payment_details.show_options")}</span>
              </AccordionTrigger>
            </AccordionItem>
          </Accordion>
        </div>
        <PayButton
          onPay={onPay}
          payments={uninitiatedDraftPayments}
          currency={currency}
        />
      </CardContent>
    </Card>
  );

  function useShowPaymentOptions() {
    const [showDiscount, setShowDiscount] = useState(hasDiscount);
    const [showPartialPayment, setShowPartialPayment] =
      useState(hasPartialPayment);
    const [showIban, setShowEditIban] = useState(editIban);
    const [showExecutionDate, setShowExecutionDate] =
      useState(editExecutionDate);

    useEffect(() => {
      if (!paymentOptionsAccordionOpen) {
        setShowDiscount(hasDiscount);
        setShowPartialPayment(hasPartialPayment);
        setShowEditIban(editIban);
        setShowExecutionDate(editExecutionDate);
      }
    }, [
      hasDiscount,
      hasPartialPayment,
      paymentOptionsAccordionOpen,
      editExecutionDate,
      editIban,
    ]);

    return {
      showDiscount,
      showPartialPayment,
      showIban,
      showExecutionDate,
    };
  }
};

export const PaymentForm = memo(_PaymentForm);

type PayButtonProps = {
  onPay?: () => Promise<void>;
  currency: string;
  payments: InvoiceDetailsDraftPayment[];
};

const PayButton = ({ onPay, payments, currency }: PayButtonProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handlePay = async () => {
    try {
      setIsLoading(true);
      await onPay?.();
    } catch (error) {
      toast.error(t("generic_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const paymentsAmount = useMemo(() => {
    return payments.reduce((acc, payment) => {
      return acc + (payment.amount ?? 0);
    }, 0);
  }, [payments]);

  return (
    <Button
      disabled={isLoading}
      onClick={handlePay}
      variant="outline"
      className="gap-2"
    >
      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
      <SendHorizonal className="w-4 h-4" />
      {t("pay", { amount: formatAmount(paymentsAmount, currency ?? "EUR") })}
    </Button>
  );
};
